import { Popover } from "@headlessui/react";
import { MinusIcon, PlusIcon } from "@heroicons/react/outline";
import { XIcon } from "@heroicons/react/solid";
import Modal from "components/modal/Modal";
import {
  addDiscountToItem,
  addToCart,
  removeFromCart,
  setGlobalAssignType,
  setGlobalSelectedSerializedProduct,
} from "features/cart/CartSlice";
import customAxios from "middleware/customAxios";
import React from "react";
import { MdDelete, MdRemove } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

// Filter discounts to only show active ones (not expired)
const filterActiveDiscounts = (discountsList) => {
  const today = moment().startOf("day");
  return discountsList.filter((discount) => {
    // If no dates are set, consider it active
    if (!discount.startDate && !discount.endDate) {
      return true;
    }
    
    // Check start date - discount should have started
    if (discount.startDate) {
      const startDate = moment(discount.startDate).startOf("day");
      if (startDate.isAfter(today)) {
        return false; // Discount hasn't started yet
      }
    }
    
    // Check end date - discount should not have expired
    if (discount.endDate) {
      const endDate = moment(discount.endDate).endOf("day");
      if (endDate.isBefore(today)) {
        return false; // Discount has expired
      }
    }
    
    return true; // Discount is active
  });
};

// Check if a discount is applicable to a specific item.
// Only show discounts that either apply to all products or explicitly include this product variant.
const isDiscountApplicableToItem = (discount, item) => {
  // If discount is applicable to all products, show it for any item
  if (
    discount.products === "All Products" ||
    discount.isApplicableToAllProducts === true
  ) {
    return true;
  }

  // If no products specified, do not show this discount for this item
  if (!discount.products || discount.products.trim() === "" || discount.products === "—") {
    return false;
  }

  // Build the item's full product label (same format as API: "Product Name - Variant Name")
  const itemFullName = `${item?.productName ?? ""} - ${item?.name ?? ""}`.trim();
  if (!itemFullName) return false;

  // Discount's products is a comma-separated list; require exact match with one entry
  const applicableProducts = discount.products
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  return applicableProducts.some((productLabel) => productLabel === itemFullName);
};

// Check if a discount is applicable to the current user.
// If discount has selectedUserIds, only show it if current user ID is in that list.
// If selectedUserIds is null/empty, show for all users.
const isDiscountApplicableToUser = (discount, currentUserId) => {
  // If no user restriction (null/empty), discount applies to all users
  if (!discount.selectedUserIds || String(discount.selectedUserIds).trim() === "") {
    return true;
  }

  // If no current user ID, don't show user-restricted discounts
  if (!currentUserId) {
    return false;
  }

  try {
    // Parse JSON array of user IDs
    const userIds = JSON.parse(discount.selectedUserIds);
    if (!Array.isArray(userIds) || userIds.length === 0) {
      return true; // Empty array = all users
    }
    // Check if current user ID is in the list
    return userIds.includes(Number(currentUserId)) || userIds.includes(String(currentUserId));
  } catch (e) {
    // If parsing fails, treat as "all users" to be safe
    console.warn("Failed to parse discount.selectedUserIds:", e);
    return true;
  }
};

export default function OrderItemCard({ item }) {
  const [discounts, setDiscounts] = React.useState([]);
  const [showDiscountModal, setShowDiscountModal] = React.useState(false);
  const [selectedDiscount, setSelectedDiscount] = React.useState(undefined);

  let dispatch = useDispatch();
  const userData = useSelector((state) => state.data.userData);
  const currentUserId = userData?.id || userData?.userId || null;

  React.useEffect(() => {
    customAxios.get("/admin/discounts").then((res) => {
      const allDiscounts = Array.isArray(res.data) ? res.data : [];
      const activeDiscounts = filterActiveDiscounts(allDiscounts);
      // Filter by product match AND user match
      const applicableDiscounts = activeDiscounts.filter((discount) => {
        const matchesProduct = isDiscountApplicableToItem(discount, item);
        const matchesUser = isDiscountApplicableToUser(discount, currentUserId);
        return matchesProduct && matchesUser;
      });
      // Always include the currently applied discount in the list so it shows when reopening the modal
      const applied = item?.appliedDiscount;
      if (applied && !applicableDiscounts.some((d) => d.id === applied.id)) {
        setDiscounts([applied, ...applicableDiscounts]);
      } else {
        setDiscounts(applicableDiscounts);
      }
    });
  }, [item, currentUserId]);

  return (
    <div className="relative grid grid-cols-12 border gap- bg-white p-2 rounded-md">
      {/* <pre>{JSON.stringify(item, null, 2)}</pre> */}
      {/* // Order item */}
      <div className="flex flex-row space-x-3 col-span-5">
        <div
          style={{
            backgroundImage: `url(${process.env.REACT_APP_SERVER_URL + item?.image
              })`,
          }}
          className="bg-cover w-20 h-20 bg-center bg-white rounded-xl flex items-center justify-center"
        />
        <div className=" flex justify-center items-start flex-col space-y-0">
          <h1 className="text-sm font-light">{item?.productName}</h1>
          <h1 className="text-sm font-bold">{item?.name}</h1>

          {/* // Price */}
          <div className=" ">
            {item.appliedDiscount !== undefined && (
              <p className="text-slate-800 line-through">MVR {item?.price}</p>
            )}

            <p className="text-slate-800 ">
              MVR {item?.discountPrice ?? item.price}
            </p>
          </div>
        </div>
      </div>
      {/* discount */}
      <div className="col-span-4 flex items-center justify-center">
        <div>
          {(selectedDiscount ?? item?.appliedDiscount) == null ? (
            <button
              onClick={() => setShowDiscountModal(true)}
              className="p-1 px-2 border bg-white border-slate-400 rounded-lg text-[10px]"
            >
              No Selected Discount
            </button>
          ) : (
            <p
              onClick={() => setShowDiscountModal(true)}
              className="p-1 px-2 border bg-white uppercase font-medium border-slate-400 rounded-lg text-sm"
            >
              {(selectedDiscount ?? item?.appliedDiscount).name}
            </p>
          )}
        </div>
      </div>
      {/* // Quantity */}
      {!item.isSerialized ? (
        <div className="col-span-3 flex justify-center items-center flex-col space-y-1">
          <div className="flex flex-row space-x-3  p-1.5 bg-white rounded-full h-fit justify-center items-center">
            {/* Minus Button */}
            {item?.quantity === 1 ? (
              <div
                onClick={() => {
                  dispatch(
                    removeFromCart({
                      id: item.id,
                      image: item.image,
                      productName: item.variantName,
                      name: item.name,
                      price: item.price,
                      unitPrice: item.unitPrice,
                      quantity: 1,
                    })
                  );
                }}
                className="flex items-center hover:cursor-pointer hover:ring-2 ring-yellow-500 ring-offset-2 justify-center text-white p-1.5 rounded-full bg-red-500"
              >
                <MdDelete className="w-4 h-4 rounded-full text-primary" />
              </div>
            ) : (
              <button
                onClick={() => {
                  dispatch(
                    removeFromCart({
                      id: item.id,
                      image: item.image,
                      productName: item.variantName,
                      name: item.name,
                      price: item.price,
                      unitPrice: item.unitPrice,
                      quantity: 1,
                    })
                  );
                }}
                className="flex items-center hover:cursor-pointer hover:ring-2 ring-yellow-500 active:brightness-110 ring-offset-2 justify-center text-white p-1.5 rounded-full bg-yellow-500"
              >
                <MinusIcon className="w-4 h-4 rounded-full text-slate-50" />
              </button>
            )}

            {/* Quantity */}
            <div className="flex items-center justify-center ">
              <p className="text-slate-900">{item?.quantity}</p>
            </div>

            {/* Plus Button */}
            <div
              onClick={() => {
                dispatch(
                  addToCart({
                    id: item.id,
                    image: item.image,
                    productName: item.variantName,
                    name: item.name,
                    price: item.price,
                    quantity: 1,
                  })
                );
              }}
              className="flex items-center hover:cursor-pointer hover:ring-2 ring-yellow-500 active:brightness-110 ring-offset-2 justify-center text-white p-1.5 rounded-full bg-yellow-500"
            >
              <PlusIcon className="w-4 h-4 " />
            </div>
          </div>
        </div>
      ) : (
        <div className="col-span-3 flex justify-center items-center flex-col space-y-1">
          <div className="flex flex-row space-x-3  p-1.5 bg-white rounded-full h-fit justify-center items-center">
            <div
              onClick={() => {
                dispatch(
                  removeFromCart({
                    id: item.id,
                    image: item.image,
                    productName: item.variantName,
                    name: item.name,
                    price: item.price,
                    unitPrice: item.unitPrice,
                    quantity: 1,
                  })
                );
                dispatch(setGlobalAssignType(null));
                dispatch(setGlobalSelectedSerializedProduct(null));
              }}
              className="flex items-center hover:cursor-pointer hover:ring-2 ring-yellow-500 ring-offset-2 justify-center text-white p-1.5 rounded-full bg-red-500"
            >
              <MdDelete className="w-4 h-4 rounded-full text-primary" />
            </div>
          </div>
        </div>
      )}

      <Modal
        heading={"Select Discount"}
        show={showDiscountModal}
        setShow={setShowDiscountModal}
      >
        <div className="p-6">
          {/* Product Name Header */}
          <div className="mb-4 pb-3 border-b border-slate-200">
            <h2 className="text-sm text-slate-600 font-medium mb-1">Selected Product</h2>
            <h3 className="text-lg font-bold text-slate-900">
              {item?.productName} - {item?.name}
            </h3>
          </div>

          {/* Show message if no applicable discounts */}
          {discounts.length === 0 && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                No applicable discounts found for this product. Only discounts that match this product will be shown.
              </p>
            </div>
          )}

          {/* Discount Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {discounts.map((discount) => {
              const isSelected = (selectedDiscount ?? item?.appliedDiscount)?.id === discount?.id;
              return (
              <div
                key={discount.id}
                onClick={() => {
                  setSelectedDiscount(discount);
                  setShowDiscountModal(false);
                  dispatch(
                    addDiscountToItem({
                      id: item.id,
                      image: item.image,
                      productName: item.variantName,
                      name: item.name,
                      price: item.price,
                      unitPrice: item.unitPrice,
                      quantity: item.quantity,
                      appliedDiscount: discount,
                      quantity: item.quantity,
                    })
                  );
                }}
                className={`${isSelected
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-900 border-slate-200 hover:border-slate-400"
                  } flex flex-col items-start cursor-pointer space-y-3 p-5 shadow-md border-2 rounded-xl transition-all`}
              >
                {/* Discount Title */}
                <div className="w-full">
                  <h3 className="text-base font-bold mb-1">{discount.name}</h3>
                  <p className="text-xs opacity-75">
                    {discount.method === "code" ? "Discount Code" : "Automatic Discount"}
                  </p>
                </div>

                {/* Product Name(s) */}
                <div className="w-full">
                  <p className="text-xs font-medium mb-1 opacity-75">Applicable Products:</p>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold">
                      {discount.products === "All Products" || discount.isApplicableToAllProducts
                        ? "All Products"
                        : discount.products || "—"}
                    </p>
                    {(discount.products === "All Products" || discount.isApplicableToAllProducts) && (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full font-medium">
                        Universal
                      </span>
                    )}
                  </div>
                  {/* Show match indicator */}
                  {!discount.isApplicableToAllProducts && discount.products !== "All Products" && (
                    <p className="text-xs text-green-600 mt-1 font-medium">
                      ✓ Matches this product
                    </p>
                  )}
                </div>

                {/* Discount Value */}
                <div className="w-full pt-2 border-t border-slate-300">
                  <p className="text-xs font-medium mb-1 opacity-75">Discount:</p>
                  <p className="text-lg font-bold">
                    {discount?.fixedAmount !== null && discount?.fixedAmount !== undefined
                      ? `MVR ${discount.fixedAmount}`
                      : discount?.percentage
                        ? `${discount.percentage}% OFF`
                        : "—"}
                  </p>
                </div>

                {/* Valid Period */}
                {(discount.startDate || discount.endDate) && (
                  <div className="w-full pt-2 border-t border-slate-300">
                    <p className="text-xs font-medium mb-1 opacity-75">Valid Period:</p>
                    <p className="text-xs">
                      {discount.startDate
                        ? moment(discount.startDate).format("DD MMM YYYY")
                        : "—"}
                      {" - "}
                      {discount.endDate
                        ? moment(discount.endDate).format("DD MMM YYYY")
                        : "No expiry"}
                    </p>
                  </div>
                )}
              </div>
            );
            })}
          </div>

          {/* Remove Discount Button */}
          {(selectedDiscount ?? item?.appliedDiscount) && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <button
                onClick={() => {
                  setSelectedDiscount(undefined);
                  setShowDiscountModal(false);
                  dispatch(
                    addDiscountToItem({
                      id: item.id,
                      image: item.image,
                      productName: item.variantName,
                      name: item.name,
                      price: item.price,
                      unitPrice: item.unitPrice,
                      quantity: item.quantity,
                      appliedDiscount: undefined,
                    })
                  );
                }}
                className="w-full px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg transition-colors"
              >
                Remove Discount
              </button>
            </div>
          )}
        </div>

        <button
          onClick={() => setShowDiscountModal(false)}
          className="fixed top-6 right-6 z-50 p-2 hover:bg-slate-100 rounded-full transition-colors"
        >
          <XIcon className="w-5 h-5 text-slate-600" />
        </button>
      </Modal>
      {/* Sub total and Tax */}
    </div>
  );
}
